import { ChangeDetectionStrategy, Component, forwardRef, model, signal } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { Selectable, Weekday, getWeekdays } from '@at/shared';

@Component({
  selector: 'at-weekdays-picker',
  standalone: true,
  imports: [ReactiveFormsModule],
  templateUrl: './weekdays-picker.component.html',
  styleUrl: './weekdays-picker.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => WeekdaysPickerComponent),
      multi: true,
    },
  ],
  host: {
    '[ariaDisabled]': 'disabled()',
    '[attr.inert]': 'disabled() || null',
  },
})
export class WeekdaysPickerComponent implements ControlValueAccessor {
  #onChanged: (value: number[] | null) => void = () => {};
  #onTouched: () => void = () => {};
  // #locale = inject(LOCALE_ID);

  // keeping EN only as for now.
  protected weekdays = signal<Selectable<Weekday, number>[]>(getWeekdays('en'));
  public value = model<number[] | null>(null);
  public disabled = model(false);

  protected toggleWeekday(weekday: Weekday, checkInput: HTMLInputElement) {
    const value = this.value() ?? [];
    if (checkInput.checked) {
      this.value.set([...value, weekday.id]);
    } else {
      const newValue = value.filter((id) => id !== weekday.id);
      this.value.set(newValue.length ? newValue : null);
    }
    this.#onTouched();
    this.#onChanged(this.value());
  }

  public writeValue(value: number[] | null): void {
    this.value.set(value);
    this.weekdays.update((selection) =>
      selection.map((weekday) => ({
        ...weekday,
        selected: value?.includes(weekday.id) ?? false,
      })),
    );
  }

  public registerOnChange(fn: (value: number[] | null) => void): void {
    this.#onChanged = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.#onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }
}
